import { pushEvent } from 'helpers/utils/dataLayer';
import { AddToWishlistResponse, GamoogaForAddToWishlist } from './transformer';
import { CohortSaleTemplate, PrimaryCategories } from 'store/product/types';
import { events } from 'constants/dataLayer';
import {
  getFeaturedString,
  getStockString,
} from 'shared/store/dataLayer/helpers';
import { CURRENCY, NO_OFFER_ID, NO_OFFER_MESSAGE } from 'shared/constants';
import { OfferData } from 'types/productCard';
import { ProductWidgetTrackingProps } from 'components/CartOperations';
import { transformDataforMixPanelTracking } from 'components/CartOperations/helper';

interface WishlistForDataLayer {
  productId: string;
  childId: string;
  parentId: string;
  sku?: string;
  categoryLevel?: PrimaryCategories;
  brandName: string;
  mrp: number;
  productName: string;
  offerPrice: number;
  discount: number;
  gamoogaData: GamoogaForAddToWishlist;
  featured: string;
  pageSource: string;
  inStock: string;
  isLux?: number;
  currency: string;
  tags: string;
  videoId?: string;
  type: string;
  variantType: string;
  variantCount: number;
  trackingData?: string;
  plpPriceRevealWishlist?: boolean;
  inStockVariants?: number;
  oosVariants?: number;
  cohortSaleTemplate?: CohortSaleTemplate | null;
  cohortSaleTemplateV2?: CohortSaleTemplate | null;
  gptConversationId?: string;
  isConvSearch?: boolean;
}

interface WishlistOptionalData {
  [key: string]: string;
}

const pushWishlist = (
  data: WishlistForDataLayer,
  optionalData: WishlistOptionalData = {},
  productWidgetProps?: ProductWidgetTrackingProps,
  appTrackingKeys?: Record<string, any>
) => {
  let payload = {
    wishlistAddition: data,
    ...optionalData,
  };
  if (data?.isConvSearch) {
    const { search_term, ui_design_version, active_tab } =
      appTrackingKeys || {};
    payload = {
      ...payload,
      ...appTrackingKeys,
      listing_opened: `Search:query:${search_term}`,
      listing_type: 'searchListingPage',
      listing_value: search_term,
      search_keyword: search_term,
      has_nykaa_gpt_interaction: true,
      gpt_conversation_id: data?.gptConversationId || '',
      plp_click_count: window.datalayer?.plpInteractionData?.clickCount || 0,
      ...(ui_design_version && { ui_design_version }),
      ...(active_tab && { active_tab }),
    };
  }
  pushEvent(events.ADD_TO_WISHLIST_SUCCESS, {
    wishlistAddition: data,
    ...optionalData,
  });
  transformDataforMixPanelTracking(
    payload,
    'wishlistAddition',
    productWidgetProps
  );
};

interface WishlistTrackingData extends AddToWishlistResponse {
  product: {
    id: string;
    brandName: string;
    mrp: number;
    name: string;
    offerPrice: number;
    primaryCategories?: PrimaryCategories;
    featured: boolean;
    inStock: boolean;
    parentId: string;
    isLux?: number;
    sku?: string;
    tags: string;
    offerMessage?: string;
    offerId?: string;
    showOffer: boolean;
    inStockVariants?: number;
    oosVariants?: number;
    cohortSaleTemplate?: CohortSaleTemplate | null;
    cohortSaleTemplateV2?: CohortSaleTemplate | null;
  };
  pageSource: string;
  videoId?: string;
  trackingData?: string;
  plpPriceRevealWishlist?: boolean;
  gptConversationId?: string;
  isConvSearch?: boolean;
  appTrackingKeys?: Record<string, any>;
  interactionInfo?: Record<string, any>;
}

export const trackAddToWishlist = (
  wishlistData: WishlistTrackingData,
  productWidgetProps?: ProductWidgetTrackingProps
) => {
  const {
    gamoogaList,
    product,
    pageSource,
    videoId,
    trackingData,
    plpPriceRevealWishlist,
    gptConversationId,
    isConvSearch,
    appTrackingKeys,
    interactionInfo,
  } = wishlistData;
  const {
    offerMessage = NO_OFFER_MESSAGE,
    offerId = NO_OFFER_ID,
    showOffer = false,
  } = product;
  const offerData: OfferData = {};
  if (showOffer) {
    offerData.offerMessage = offerMessage;
    offerData.offerId = offerId;
  }
  pushWishlist(
    {
      gamoogaData: gamoogaList,
      childId: product.id,
      productId: product.parentId,
      sku: product.sku,
      parentId: product.parentId,
      brandName: product.brandName,
      mrp: product.mrp,
      productName: product.name,
      offerPrice: product.offerPrice,
      discount: product.discount,
      categoryLevel: product.primaryCategories,
      isLux: product.isLux,
      featured: getFeaturedString(product.featured),
      tags: product?.tags,
      pageSource: pageSource,
      inStock: getStockString(product.inStock),
      currency: CURRENCY,
      type: product.type,
      variantCount: product.variantCount,
      variantType: product.variantType,
      inStockVariants: product.inStockVariants,
      oosVariants: product.oosVariants,
      cohortSaleTemplate: product.cohortSaleTemplate,
      cohortSaleTemplateV2: product.cohortSaleTemplateV2,
      videoId,
      trackingData,
      plpPriceRevealWishlist,
      gptConversationId,
      isConvSearch,
    },
    offerData,
    productWidgetProps,
    appTrackingKeys,
    interactionInfo
  );
};

interface RemoveWishlistDataLayer {
  childId: string;
  productId: string;
  featured: string;
  inStock: string;
  currency: string;
  discount: number;
  brandName: string;
  mrp: number;
  productName: string;
  offerPrice: number;
  parentId: string;
  sku?: string;
  tags: string;
  type: string;
  variantType: string;
  variantCount: number;
  pageSource?: string;
  categoryLevel?: PrimaryCategories;
  trackingData?: string;
  inStockVariants?: number;
  oosVariants?: number;
  gptConversationId?: string;
  isConvSearch?: boolean;
}

const pushWishlistRemove = (
  data: RemoveWishlistDataLayer,
  optional: WishlistOptionalData = {},
  productWidgetProps?: ProductWidgetTrackingProps,
  appTrackingKeys?: Record<string, any>
) => {
  const payload = {
    wishlistRemove: data,
    ...optional,
  };
  pushEvent(events.REMOVE_ROM_WISHLIST, {
    wishlistRemove: data,
    ...optional,
  });
  const {
    ui_design_version,
    active_tab,
    site_navigation,
    site_sub_navigation,
  } = appTrackingKeys || {};
  if (data?.isConvSearch) {
    payload['discounted_percent'] = data?.discount;
    payload['interaction_location'] =
      appTrackingKeys?.interaction_location || '';
    payload['listing_type'] = 'searchListingPage';
    payload['listing_value'] = appTrackingKeys?.search_term || '';
    payload['plp_click_count'] =
      window?.datalayer?.plpInteractionData?.clickCount || 0;
    payload['search_keyword'] = appTrackingKeys?.search_term || '';
    payload['has_nykaa_gpt_interaction'] = true;
    payload['gpt_conversation_id'] = data?.gptConversationId || '';
    if (ui_design_version) {
      payload['ui_design_version'] = ui_design_version;
    }
    if (active_tab) {
      payload['active_tab'] = active_tab;
    }
    if (site_navigation) {
      payload['site_navigation'] = site_navigation;
    }
    if (site_sub_navigation) {
      payload['site_sub_navigation'] = site_sub_navigation;
    }
  }
  transformDataforMixPanelTracking(
    payload,
    'wishlistRemove',
    productWidgetProps
  );
};

interface RemoveWishlistData {
  product: {
    id: string;
    featured: boolean;
    inStock: boolean;
    brandName: string;
    mrp: number;
    offerPrice: number;
    discount: number;
    parentId: string;
    name: string;
    sku?: string;
    tags: string;
    showOffer: boolean;
    offerMessage: string;
    offerId: string;
    pageSource?: string;
    categoryLevel?: PrimaryCategories;
    type: string;
    variantType: string;
    variantCount: number;
    trackingData?: string;
    inStockVariants?: number;
    oosVariants?: number;
  };
  gptConversationId?: string;
  isConvSearch?: boolean;
  appTrackingKeys?: Record<string, any>;
  interactionInfo?: any;
}

export const trackRemoveFromWishlist = (
  wishlistData: RemoveWishlistData,
  productWidgetProps?: ProductWidgetTrackingProps
) => {
  const {
    product,
    gptConversationId,
    isConvSearch,
    appTrackingKeys,
    interactionInfo,
  } = wishlistData;
  const {
    offerMessage = NO_OFFER_MESSAGE,
    offerId = NO_OFFER_ID,
    showOffer = false,
  } = product;
  const offerData: OfferData = {};
  if (showOffer) {
    offerData.offerMessage = offerMessage;
    offerData.offerId = offerId;
  }
  pushWishlistRemove(
    {
      childId: product.id,
      productId: product.parentId,
      sku: product.sku,
      featured: getFeaturedString(product.featured),
      inStock: getStockString(product.inStock),
      categoryLevel: product.categoryLevel,
      currency: CURRENCY,
      discount: product.discount,
      parentId: product.parentId,
      brandName: product.brandName,
      mrp: product.mrp,
      productName: product.name,
      offerPrice: product.offerPrice,
      tags: product?.tags,
      type: product.type,
      variantCount: product.variantCount,
      variantType: product.variantType,
      inStockVariants: product.inStockVariants,
      oosVariants: product.oosVariants,
      pageSource: product?.pageSource,
      trackingData: product?.trackingData,
      gptConversationId,
      isConvSearch,
    },
    offerData,
    productWidgetProps,
    appTrackingKeys,
    interactionInfo
  );
};
